import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

interface CancelChargeDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: (note: string) => void
  isSubmitting?: boolean
}

/** Confirm cancelling a completed leg's shipping charge; note is required. */
export const CancelChargeDialog: React.FC<CancelChargeDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  isSubmitting = false,
}) => {
  const [note, setNote] = useState('')
  const trimmed = note.trim()
  const canConfirm = trimmed.length > 0 && !isSubmitting

  const handleClose = () => {
    if (isSubmitting) return
    setNote('')
    onCancel()
  }

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm(trimmed)
    setNote('')
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel shipment charge?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This keeps the leg completed but excludes the estimated shipping price from the dashboard
          shipping audit totals. A note is required.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Cancellation note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required
          disabled={isSubmitting}
          error={note.length > 0 && trimmed.length === 0}
          helperText={note.length > 0 && trimmed.length === 0 ? 'Note cannot be blank' : ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          Keep charge
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={!canConfirm}
        >
          Cancel charge
        </Button>
      </DialogActions>
    </Dialog>
  )
}
